
const libPictView = require('pict-view');

/**
 * Artifact Type editor — the bespoke record page for an ArtifactType. Defines the artifact's
 * properties (the columns/fields an observation requirement collects). Thin shell: state in
 * pict.AppData.ArtifactTypeEditor, behavior in the ArtifactTypeEditor provider.
 */
class ArtifactTypeEditor extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get provider() { return this.pict.providers.ArtifactTypeEditor; }

	deriveRenderState() { if (this.provider) { this.provider.deriveRenderState(); } }

	onBeforeRender(pRenderable) { this.deriveRenderState(); return super.onBeforeRender(pRenderable); }

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = ArtifactTypeEditor;

module.exports.default_configuration =
{
	ViewIdentifier: 'ArtifactTypeEditor',
	DefaultRenderable: 'ArtifactTypeEditorWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		.ate-wrap { padding: 1.25rem 1.5rem 2rem; }
		.ate-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 1rem; }
		.ate-header-actions { display: flex; gap: 0.5rem; flex-shrink: 0; }
		.ate-section-head { display: flex; justify-content: space-between; align-items: center; margin: 1.5rem 0 0.5rem; }
		.ate-prop-table td, .ate-prop-table th { vertical-align: top; }
		.ate-prop-table td { padding-top: 0.45rem; }
		.ate-prop-key { min-width: 8rem; }
		.ate-order .button { padding-left: 0.5rem; padding-right: 0.5rem; }
		.ate-empty { padding: 2.5rem 1rem; }
		.ate-spinner { display: inline-block; width: 1.6rem; height: 1.6rem; border: 3px solid var(--bulma-border, #dbdbdb); border-top-color: var(--bulma-link, #485fc7); border-radius: 50%; animation: ate-spin 0.8s linear infinite; }
		@keyframes ate-spin { to { transform: rotate(360deg); } }
	`,
	Templates:
	[
		{
			Hash: 'ArtifactType-Wrap',
			Template: /*html*/`
				<div class="ate-wrap">
					{~TS:ArtifactType-Message:AppData.ArtifactTypeEditor.MessageSlot~}
					{~TS:ArtifactType-Loading:AppData.ArtifactTypeEditor.LoadingSlot~}
					{~TS:ArtifactType-Editor:AppData.ArtifactTypeEditor.EditorSlot~}
					{~TS:ArtifactType-Empty:AppData.ArtifactTypeEditor.EmptySlot~}
				</div>`
		},
		{
			Hash: 'ArtifactType-Editor',
			Template: /*html*/`
				<div class="ate-editor">
					<div class="ate-header">
						<div>
							<h1 class="title is-4">{~D:Record.TitleText~}</h1>
							<p class="subtitle is-6 has-text-grey">A reusable data-shape — its fields are the columns of a spreadsheet observation (or the fields of a journal one).</p>
						</div>
						<div class="ate-header-actions">
							<button class="button is-light" onclick="_Pict.providers.ArtifactTypeEditor.backToList()"><span class="icon">{~I:ChevronLeft~}</span><span>Back</span></button>
							{~TS:ArtifactType-SaveButton:AppData.ArtifactTypeEditor.EditorSlot~}
						</div>
					</div>

					{~TS:ArtifactType-ReadOnlyNotice:AppData.ArtifactTypeEditor.ReadOnlyNoticeSlot~}

					<div class="box">
						<div class="field">
							<label class="label is-small">Name</label>
							<div class="control"><input class="input" type="text" value="{~D:Record.Name~}" {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ArtifactTypeEditor.setArtifactName(this.value)" /></div>
						</div>
					</div>

					<div class="ate-section-head">
						<h2 class="title is-5">Fields <span class="tag is-light">{~D:Record.PropertyCount~}</span></h2>
						{~TS:ArtifactType-AddButton:AppData.ArtifactTypeEditor.EditorSlot~}
					</div>
					<p class="help mb-2">Each field is a property of the shape. <strong>Key</strong> is the data key; <strong>Type</strong> picks the input; add comma-separated <strong>Options</strong> to make it a dropdown. Order sets how columns appear.</p>

					<table class="table is-fullwidth is-narrow is-hoverable ate-prop-table">
						<thead><tr><th>Key</th><th>Display name</th><th>Type</th><th>Options (dropdown)</th><th class="has-text-centered">Required</th><th class="has-text-centered">Hidden</th><th class="has-text-right">Order</th></tr></thead>
						<tbody>{~TS:ArtifactType-PropertyRow:AppData.ArtifactTypeEditor.Properties~}</tbody>
					</table>
					{~TS:ArtifactType-EmptyProperties:AppData.ArtifactTypeEditor.EmptyPropertiesSlot~}
				</div>`
		},
		{
			Hash: 'ArtifactType-SaveButton',
			Template: /*html*/`{~TIfAbs:ArtifactType-SaveButton-Inner:Record:Record.CanEdit^TRUE^~}`
		},
		{
			Hash: 'ArtifactType-SaveButton-Inner',
			Template: /*html*/`<button class="button is-link" {~D:Record.SaveDisabledAttr~} onclick="_Pict.providers.ArtifactTypeEditor.saveAsync()"><span class="icon">{~I:Save~}</span><span>Save</span></button>`
		},
		{
			Hash: 'ArtifactType-AddButton',
			Template: /*html*/`{~TIfAbs:ArtifactType-AddButton-Inner:Record:Record.CanEdit^TRUE^~}`
		},
		{
			Hash: 'ArtifactType-AddButton-Inner',
			Template: /*html*/`<button class="button is-small is-link is-light" onclick="_Pict.providers.ArtifactTypeEditor.addProperty()"><span class="icon">{~I:Plus~}</span><span>Add field</span></button>`
		},
		{
			Hash: 'ArtifactType-PropertyRow',
			Template: /*html*/`
				<tr>
					<td><input class="input is-small ate-prop-key" type="text" placeholder="Key" value="{~D:Record.KeyName~}" {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ArtifactTypeEditor.setPropertyField('{~D:Record.Key~}','Key', this.value)" /></td>
					<td><input class="input is-small" type="text" placeholder="(optional)" value="{~D:Record.DisplayName~}" {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ArtifactTypeEditor.setPropertyField('{~D:Record.Key~}','DisplayName', this.value)" /></td>
					<td><div class="select is-small"><select {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ArtifactTypeEditor.setPropertyField('{~D:Record.Key~}','Type', this.value)">{~TS:ArtifactType-TypeOption:Record.TypeOptions~}</select></div></td>
					<td><input class="input is-small" type="text" placeholder="e.g. M, F" value="{~D:Record.EnumOptions~}" {~D:Record.EnumDisabledAttr~} onchange="_Pict.providers.ArtifactTypeEditor.setPropertyField('{~D:Record.Key~}','EnumOptions', this.value)" /></td>
					<td class="has-text-centered"><input type="checkbox" {~D:Record.RequiredAttr~} {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ArtifactTypeEditor.setPropertyField('{~D:Record.Key~}','Required', this.checked)" /></td>
					<td class="has-text-centered"><input type="checkbox" {~D:Record.HiddenAttr~} {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ArtifactTypeEditor.setPropertyField('{~D:Record.Key~}','Hidden', this.checked)" /></td>
					<td class="has-text-right ate-order">
						<button class="button is-small is-white" title="Move up" onclick="_Pict.providers.ArtifactTypeEditor.moveUp('{~D:Record.Key~}')">{~I:ArrowUp~}</button>
						<button class="button is-small is-white" title="Move down" onclick="_Pict.providers.ArtifactTypeEditor.moveDown('{~D:Record.Key~}')">{~I:ArrowDown~}</button>
						{~TIfAbs:ArtifactType-RemoveButton:Record:AppData.ArtifactTypeEditor.CanEdit^TRUE^~}
					</td>
				</tr>`
		},
		{
			Hash: 'ArtifactType-TypeOption',
			Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Value~}</option>`
		},
		{
			Hash: 'ArtifactType-RemoveButton',
			Template: /*html*/`<button class="button is-small is-white" title="Remove field" onclick="_Pict.providers.ArtifactTypeEditor.removeProperty('{~D:Record.Key~}')">{~I:Trash~}</button>`
		},
		{
			Hash: 'ArtifactType-EmptyProperties',
			Template: /*html*/`<p class="has-text-grey has-text-centered py-4">No fields yet. Use <strong>Add field</strong> to define this shape.</p>`
		},
		{
			Hash: 'ArtifactType-ReadOnlyNotice',
			Template: /*html*/`<article class="message is-warning is-small"><div class="message-body"><span class="icon">{~I:Lock~}</span> This artifact type is <strong>read-only</strong> for you — you can edit ones you created (or any, as an admin). Create a new artifact type to define your own.</div></article>`
		},
		{
			Hash: 'ArtifactType-Message',
			Template: /*html*/`<div class="notification {~D:Record.MessageClass~} is-soft py-2 px-3 mb-3">{~D:Record.Message~}</div>`
		},
		{
			Hash: 'ArtifactType-Loading',
			Template: /*html*/`<div class="ate-empty box has-text-grey has-text-centered"><span class="ate-spinner"></span><p class="mt-3">Loading…</p></div>`
		},
		{
			Hash: 'ArtifactType-Empty',
			Template: /*html*/`<div class="ate-empty box has-text-grey has-text-centered"><span class="icon is-large">{~I:Code~}</span><p class="mt-3">Pick an artifact type from the list, or create a new one.</p></div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'ArtifactTypeEditorWrap',
			TemplateHash: 'ArtifactType-Wrap',
			ContentDestinationAddress: '#PlatformConfiguration-Application-Container'
		}
	]
};
